// Project → Bills tab. Hits /projects/:id/bills which lists vendor bills
// attached to one of the project's POs (or directly to an order line).
// Read-only list in the design table grammar (other Business UI handoff,
// 2026-10-08) — review, approve and pay happen on the bill record.
// "Enter a bill" (Stephanie 2026-10-13): pick one of this project's POs; its
// pricing loads line by line to confirm against the supplier's bill.
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useOutletContext } from "react-router-dom";
import { Plus } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/auth/AuthProvider";
import { Button } from "@/components/ui/Button";
import { BillFromPoModal, type BillPoOption } from "@/components/finance/BillFromPoModal";
import { StatusPill } from "@/components/ui/StatusPill";
import { TABLE_HEAD, TABLE_WRAP, TD, TH, TROW } from "@/components/ui/RecordParts";
import { cn } from "@/lib/utils";
import type { ProjectDetailResponse } from "@/pages/ProjectDetailPage";

interface BillRow {
  id: string;
  vendorId: string;
  vendorName: string;
  purchaseOrderId: string | null;
  poNumber: number | null;
  /** 2026-06-01 — bills can attach directly to an order line
   *  (mutually exclusive with purchaseOrderId). */
  lineItemId: string | null;
  lineNumber: number | null;
  lineServiceType: string | null;
  lineDescription: string | null;
  vendorBillNumber: string | null;
  billDate: string;
  dueDate: string | null;
  status: string;
  total: string;
  paidAt: string | null;
  approvedAt: string | null;
  createdAt: string | null;
}

interface BillsListResponse {
  rows: BillRow[];
}

function statusTone(
  s: string,
): "active" | "prospect" | "dormant" | "closed" | "warn" | "info" {
  switch (s) {
    case "draft":
    case "pending_review":
      return "warn";
    case "approved":
      return "info";
    case "paid":
      return "active";
    case "disputed":
      return "warn";
    case "voided":
      return "closed";
    default:
      return "info";
  }
}

function statusLabel(s: string): string {
  switch (s) {
    case "draft":
      return "Draft";
    case "pending_review":
      return "To review";
    case "approved":
      return "Approved · not paid";
    case "disputed":
      return "Disputed";
    case "paid":
      return "Paid";
    case "voided":
      return "Voided";
    default:
      return s.replace(/_/g, " ");
  }
}

function formatMoney(s: string): string {
  const n = Number(s);
  if (!Number.isFinite(n)) return s;
  return n.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
  });
}

function formatDate(s: string | null): string {
  if (!s) return "—";
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  return new Date(s + "T00:00:00").toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function attribution(b: BillRow): string {
  if (b.poNumber !== null) return `PO #${b.poNumber}`;
  if (b.lineNumber !== null) {
    return `Line #${b.lineNumber}${
      b.lineServiceType && b.lineServiceType !== "product"
        ? ` · ${b.lineServiceType.replace(/_/g, " ")}`
        : ""
    }`;
  }
  return "Not linked to a PO";
}

export function ProjectBillsTab() {
  const data = useOutletContext<ProjectDetailResponse>();
  const navigate = useNavigate();
  const projectId = data.project.id;

  const { data: list, isLoading, isError } = useQuery({
    queryKey: ["project", projectId, "bills"],
    queryFn: () => api<BillsListResponse>(`/projects/${projectId}/bills`),
  });

  const rows = list?.rows ?? [];

  // Entering bills is AP work: admin / finance (POST /bills is gated the same).
  const { user } = useAuth();
  const canEnter = user?.roles.some((r) => r === "admin" || r === "finance") ?? false;
  const queryClient = useQueryClient();
  const [entering, setEntering] = useState(false);
  // ?bill_po=<id> (from Billing → Missing a bill): open Enter a bill on that PO.
  const [params, setParams] = useSearchParams();
  const billPo = params.get("bill_po");
  const posQ = useQuery({
    queryKey: ["project", projectId, "purchase-orders", "for-bills"],
    queryFn: () =>
      api<{ rows: Array<BillPoOption & { internalStage: string }> }>(
        `/projects/${projectId}/purchase-orders`,
      ),
    enabled: canEnter,
  });
  const billablePos: BillPoOption[] = (posQ.data?.rows ?? [])
    .filter((p) => p.internalStage !== "cancelled")
    .map(({ id, poNumber, vendorName }) => ({ id, poNumber, vendorName }));
  useEffect(() => {
    if (billPo && canEnter && billablePos.some((p) => p.id === billPo)) setEntering(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billPo, canEnter, posQ.data]);
  const closeEnter = () => {
    setEntering(false);
    if (billPo) {
      const next = new URLSearchParams(params);
      next.delete("bill_po");
      setParams(next, { replace: true });
    }
  };
  const enterButton = canEnter && billablePos.length > 0 && (
    <Button type="button" onClick={() => setEntering(true)}>
      <Plus className="h-4 w-4" /> Enter a bill
    </Button>
  );

  return (
    <section>
      <div className="mb-4 flex items-baseline justify-between gap-4">
        <h2 className="text-lg font-semibold tracking-[-0.4px] text-ink">Vendor bills</h2>
        <div className="flex items-center gap-3">
          {!isLoading && !isError && (
            <p className="text-xs text-muted">
              {rows.length} {rows.length === 1 ? "bill" : "bills"}
            </p>
          )}
          {enterButton}
        </div>
      </div>

      {isLoading ? (
        <div className={`${TABLE_WRAP} space-y-3 p-5`} aria-label="Loading bills">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-4 w-full animate-pulse rounded bg-canvas" />
          ))}
        </div>
      ) : isError ? (
        <div role="alert" className="rounded-md border border-risk/30 bg-risk-bg p-5 text-sm text-risk">
          We couldn't load this project's bills. Nothing has changed — refresh to try again.
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-md border border-rule bg-white px-5 py-12 text-center">
          <p className="font-medium text-ink">No vendor bills yet.</p>
          <p className="mt-1 text-sm text-muted">
            {canEnter && billablePos.length > 0
              ? "When a supplier invoices us, press Enter a bill and pick the PO it's for."
              : canEnter && !posQ.isLoading
                ? "Bills are entered against a PO — create this project's POs first."
                : "Bills land here once vendors invoice us against this project's POs."}
          </p>
        </div>
      ) : (
        <div className={TABLE_WRAP} role="region" aria-label="Project vendor bills" tabIndex={0}>
          <table className="w-full text-left">
            <thead className={TABLE_HEAD}>
              <tr>
                <th scope="col" className={TH}>Bill</th>
                <th scope="col" className={TH}>Status</th>
                <th scope="col" className={`${TH} hidden sm:table-cell`}>Due</th>
                <th scope="col" className={`${TH} text-right`}>Total</th>
              </tr>
            </thead>
            <tbody className="text-[13px]">
              {rows.map((b) => (
                <tr
                  key={b.id}
                  onClick={() => navigate(`/bills/${b.id}`)}
                  className={cn(TROW, "cursor-pointer")}
                >
                  <td className={TD}>
                    <Link
                      to={`/bills/${b.id}`}
                      onClick={(e) => e.stopPropagation()}
                      className="font-medium text-ink hover:underline"
                    >
                      {b.vendorName}
                    </Link>
                    <span className="block text-[11px] text-muted">
                      {b.vendorBillNumber ? `Bill ${b.vendorBillNumber} · ` : "No bill number · "}
                      {attribution(b)} · billed {formatDate(b.billDate)}
                    </span>
                  </td>
                  <td className={TD}>
                    <StatusPill className="normal-case" tone={statusTone(b.status)}>{statusLabel(b.status)}</StatusPill>
                  </td>
                  <td className={`${TD} hidden text-ink sm:table-cell`}>
                    {formatDate(b.dueDate)}
                  </td>
                  <td className={`${TD} text-right font-medium tabular-nums text-ink`}>
                    {formatMoney(b.total)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {entering && (
        <BillFromPoModal
          purchaseOrders={billablePos}
          initialPoId={billPo}
          onCancel={closeEnter}
          onCreated={() => {
            closeEnter();
            void queryClient.invalidateQueries({ queryKey: ["project", projectId, "bills"] });
          }}
        />
      )}
    </section>
  );
}
